import { useState } from 'react';
import Image from 'next/image';

import { CloseIcon } from '@/icons';
import { NextLink } from '@/atoms';
import { Button, ModalV2 as Modal } from '@/molecules';

import { useAddToCart } from '@/lib/hooks';
import { debug } from '@/lib/utils';
import FallBackIMG from '@/assets/images/fallback-product-card.jpg';

interface CrossSelling {
  open: boolean;
  setOpen: (opened: boolean) => void;
  woosbData: {
    slug: string;
    image: {
      src: string;
      alt?: string;
    };
  };
  productId: number;
  /**
   * Quantity to add to cart if the user doesn't want
   * to buy the pack.
   */
  quantityToAddToCart: number;
  /**
   * Quantity to add to pack. In the product card this value will be
   * always 1, but in the product page it will be the quantity selected by the user.
   */
  quantityToAddToPack: number;
}

const CrossSelling = ({
  open,
  setOpen,
  woosbData,
  productId,
  quantityToAddToCart,
  quantityToAddToPack,
}: CrossSelling) => {
  const { add } = useAddToCart();
  const [addingToCart, setAddingToCart] = useState(false);
  const [addingToPack, setAddingToPack] = useState(false);

  return (
    <Modal open={open} setOpen={setOpen} maxWidth="max-w-lg">
      <div className="relative">
        <div className="w-full space-y-4 bg-bg-secondary p-8 shadow-xl">
          <h3 className="text-center">Completa tu cesta</h3>

          <p className="u-body u-body--m text-center text-secondary-500">
            La compra mínima de este producto son {quantityToAddToCart}{' '}
            unidades. Si quieres solo 1 unidad, puedes crear tu propio pack con
            otras referencias.
          </p>

          <Image
            src={woosbData.image.src || FallBackIMG.src}
            alt={woosbData.image?.alt || 'Fallback IMG'}
            height={200}
            width={200}
            draggable={false}
            className="mx-auto object-cover object-center"
          />

          <div className="!mt-6 flex flex-row justify-between gap-4">
            <Button
              disabled={addingToCart}
              className="uppercase"
              onClick={async () => {
                setAddingToCart(true);
                await add({
                  productId,
                  quantity: quantityToAddToCart,
                  gtm: { listName: 'product' },
                }).catch((error) => debug(error));
                setAddingToCart(false);
                setOpen(false);
              }}
            >
              {addingToCart
                ? 'Añadiendo...'
                : `Añadir ${quantityToAddToCart} uds.`}
            </Button>

            {!!woosbData.slug && (
              <NextLink
                href={`/producto/${woosbData.slug}?add_product=${productId}&product_quantity=${quantityToAddToPack}`}
                passHref
              >
                <Button
                  onClick={() => setAddingToPack(true)}
                  as="a"
                  disabled={addingToPack}
                  variant="tertiary"
                  className="uppercase !text-white"
                >
                  {addingToPack ? 'Añadiendo...' : 'Pack personalizado'}
                </Button>
              </NextLink>
            )}
          </div>
        </div>
        <div
          className="absolute right-4 top-4"
          onPointerDown={() => setOpen(false)}
        >
          <CloseIcon className="cursor-pointer" />
        </div>
      </div>
    </Modal>
  );
};

export default CrossSelling;
